import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Card, Table, Form, Button, Modal, Alert, Spinner, Badge, InputGroup, Row, Col } from 'react-bootstrap';
import Select from '../../components/common/Select';
import { useQueryClient } from '@tanstack/react-query';
import { getRoleBadgeVariant, getRoleDisplayName } from '../../utils/roles';
import {
  getUsers,
  useGetPayrollActivitiesAll,
  useGetPayrollUsersConfigs,
  usePutPayrollUsersUserIdConfig,
  useGetPayrollUsersUserIdRates,
  usePostPayrollUsersUserIdRates,
  usePutPayrollUsersUserIdRatesRateId,
  useDeletePayrollUsersUserIdRatesRateId,
  useGetPayrollUsersUserIdActivities,
  usePutPayrollUsersUserIdActivitiesActivityId,
  useDeletePayrollUsersUserIdActivitiesActivityId,
  useGetPayrollActivitiesActivityIdRates,
  useGetPayees,
  usePutPayeesIdUser,
  useGetUsersUserIdSalesQuota,
  usePutUsersUserIdSalesQuota,
  deleteCommissionsQuotasId,
  getGetPayrollUsersConfigsQueryKey,
  getGetPayeesQueryKey,
  getGetPayrollUsersUserIdRatesQueryKey,
  getGetPayrollUsersUserIdActivitiesQueryKey,
  getGetUsersUserIdSalesQuotaQueryKey,
} from '../../api/generated/arrayscapeGamingDashboardAPI';
import { UserTimesheetConfig, UserActivityRate, UserActivity, PayPeriodType, PayrollActivity, Payee, SalesQuota, User } from '../../api/generated/model';
import type { GetUsersParams } from '../../api/generated/model/getUsersParams';
import { usePaginatedQuery } from '../../hooks/usePaginatedQuery';

const FETCH_LIMIT = 100;

// Unified rate entry for display
type RateEntry = {
  type: 'default' | 'user';
  id?: string;
  hourly_rate: number;
  start_date: string;
  userRate?: UserActivityRate; // original object for edit/delete
};

// Per-activity row component that fetches its own default rates
const ActivityRateRows: React.FC<{
  activity: PayrollActivity;
  userId: string;
  isEnabled: boolean;
  isDefaultOnTimesheet: boolean;
  isToggling: boolean;
  isQBPayee: boolean;
  userRates: UserActivityRate[];
  onToggleActivity: (activityId: string, enable: boolean) => void;
  onToggleDefault: (activityId: string, defaultOn: boolean) => void;
  onAddRate: (activity: PayrollActivity) => void;
  onEditRate: (activity: PayrollActivity, rate: UserActivityRate) => void;
  onDeleteRate: (rate: UserActivityRate) => void;
}> = ({ activity, userId, isEnabled, isDefaultOnTimesheet, isToggling, isQBPayee, userRates, onToggleActivity, onToggleDefault, onAddRate, onEditRate, onDeleteRate }) => {
  const activityId = activity.id || '';
  const { data: defaultRatesData } = useGetPayrollActivitiesActivityIdRates(activityId);
  const defaultRates = defaultRatesData?.data || [];
  const hasDefaultRates = defaultRates.length > 0;
  const qbBlocked = isQBPayee && hasDefaultRates;

  // Merge default and user rates, sorted by start_date DESC
  // Only include default rates that aren't overridden by a user rate
  // (a default rate is overridden if any user rate has start_date <= that default's start_date)
  const allRates = useMemo(() => {
    const entries: RateEntry[] = [];
    for (const dr of defaultRates) {
      const drDate = dr.start_date || '';
      const isOverridden = userRates.some(ur => (ur.start_date || '') <= drDate);
      if (!isOverridden) {
        entries.push({
          type: 'default',
          id: dr.id,
          hourly_rate: dr.hourly_rate ?? 0,
          start_date: drDate,
        });
      }
    }
    for (const ur of userRates) {
      entries.push({
        type: 'user',
        id: ur.id,
        hourly_rate: ur.hourly_rate ?? 0,
        start_date: ur.start_date || '',
        userRate: ur,
      });
    }
    entries.sort((a, b) => b.start_date.localeCompare(a.start_date));
    return entries;
  }, [defaultRates, userRates]);

  if (allRates.length === 0) {
    // No rates at all — single row
    return (
      <tr className={!isEnabled ? 'text-muted' : ''}>
        <td>
          <Form.Check
            type="switch"
            checked={isEnabled}
            disabled={isToggling || qbBlocked}
            onChange={(e) => onToggleActivity(activityId, e.target.checked)}
          />
        </td>
        <td>
          {activity.name}
          {qbBlocked && (
            <div className="text-muted small fst-italic">Has per-activity rates (QB payee)</div>
          )}
        </td>
        <td>
          {isEnabled && (
            <Form.Check
              type="switch"
              checked={isDefaultOnTimesheet}
              disabled={isToggling}
              onChange={(e) => onToggleDefault(activityId, e.target.checked)}
              label="Default"
            />
          )}
        </td>
        <td className="fst-italic text-muted">{isQBPayee ? 'Rate managed in QuickBooks' : 'no rates'}</td>
        <td>-</td>
        <td>
          {isEnabled && !isQBPayee && (
            <Button variant="outline-primary" size="sm" onClick={() => onAddRate(activity)} title="Add rate">
              <i className="bi bi-plus-lg"></i>
            </Button>
          )}
        </td>
      </tr>
    );
  }

  return (
    <>
      {allRates.map((entry, idx) => (
        <tr key={`${entry.type}-${entry.id}`} className={!isEnabled ? 'text-muted' : ''}>
          {idx === 0 ? (
            <>
              <td rowSpan={allRates.length}>
                <Form.Check
                  type="switch"
                  checked={isEnabled}
                  disabled={isToggling || qbBlocked}
                  onChange={(e) => onToggleActivity(activityId, e.target.checked)}
                />
              </td>
              <td rowSpan={allRates.length}>
                {activity.name}
                {qbBlocked && (
                  <div className="text-muted small fst-italic">Has per-activity rates (QB payee)</div>
                )}
              </td>
              <td rowSpan={allRates.length}>
                {isEnabled && (
                  <Form.Check
                    type="switch"
                    checked={isDefaultOnTimesheet}
                    disabled={isToggling}
                    onChange={(e) => onToggleDefault(activityId, e.target.checked)}
                    label="Default"
                  />
                )}
              </td>
            </>
          ) : null}
          <td>
            <span className={entry.type === 'user' ? 'fw-semibold' : ''}>
              ${entry.hourly_rate.toFixed(2)}/hr
            </span>
            {entry.type === 'default' && (
              <span className="text-muted fst-italic ms-1">(default)</span>
            )}
          </td>
          <td>{entry.start_date}</td>
          <td>
            {isEnabled && !isQBPayee && entry.type === 'user' && entry.userRate && (
              <>
                {idx === 0 && (
                  <Button variant="outline-primary" size="sm" className="me-1" onClick={() => onAddRate(activity)} title="Add rate">
                    <i className="bi bi-plus-lg"></i>
                  </Button>
                )}
                <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => onEditRate(activity, entry.userRate!)} title="Edit rate">
                  <i className="bi bi-pencil"></i>
                </Button>
                <Button variant="outline-danger" size="sm" onClick={() => onDeleteRate(entry.userRate!)} title="Delete rate">
                  <i className="bi bi-trash"></i>
                </Button>
              </>
            )}
            {isEnabled && !isQBPayee && idx === 0 && entry.type === 'default' && (
              <Button variant="outline-primary" size="sm" onClick={() => onAddRate(activity)} title="Add rate">
                <i className="bi bi-plus-lg"></i>
              </Button>
            )}
          </td>
        </tr>
      ))}
    </>
  );
};

// Sub-component: expanded activity rows for a single user
const UserActivitiesSection: React.FC<{
  userId: string;
  activities: PayrollActivity[];
  isQBPayee: boolean;
}> = ({ userId, activities, isQBPayee }) => {
  const queryClient = useQueryClient();
  const { data: ratesData, isLoading: ratesLoading } = useGetPayrollUsersUserIdRates(userId);
  const { data: enabledData, isLoading: enabledLoading } = useGetPayrollUsersUserIdActivities(userId);
  const createRateMutation = usePostPayrollUsersUserIdRates();
  const updateRateMutation = usePutPayrollUsersUserIdRatesRateId();
  const deleteRateMutation = useDeletePayrollUsersUserIdRatesRateId();
  const enableActivityMutation = usePutPayrollUsersUserIdActivitiesActivityId();
  const disableActivityMutation = useDeletePayrollUsersUserIdActivitiesActivityId();

  const [showModal, setShowModal] = useState(false);
  const [modalActivity, setModalActivity] = useState<PayrollActivity | null>(null);
  const [editingRate, setEditingRate] = useState<UserActivityRate | null>(null);
  const [formData, setFormData] = useState({ hourly_rate: '', start_date: '' });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [togglingActivity, setTogglingActivity] = useState<string | null>(null);

  const rates = ratesData?.data || [];
  const userActivities: UserActivity[] = enabledData?.data || [];
  const enabledActivityIds = new Set(userActivities.map(ua => ua.activity_id || ''));
  const defaultActivityIds = new Set(userActivities.filter(ua => ua.default_on_timesheet).map(ua => ua.activity_id || ''));

  // Build a map: activityId -> all user rates
  const ratesByActivity = new Map<string, UserActivityRate[]>();
  for (const r of rates) {
    if (!r.activity_id) continue;
    const list = ratesByActivity.get(r.activity_id) || [];
    list.push(r);
    ratesByActivity.set(r.activity_id, list);
  }

  const invalidateRates = () => {
    queryClient.invalidateQueries({ queryKey: getGetPayrollUsersUserIdRatesQueryKey(userId) });
  };

  const invalidateEnabled = () => {
    queryClient.invalidateQueries({ queryKey: getGetPayrollUsersUserIdActivitiesQueryKey(userId) });
  };

  const handleToggleActivity = async (activityId: string, enable: boolean) => {
    setTogglingActivity(activityId);
    setError(null);
    try {
      if (enable) {
        await enableActivityMutation.mutateAsync({ userId, activityId, data: {} });
      } else {
        await disableActivityMutation.mutateAsync({ userId, activityId });
      }
      invalidateEnabled();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update activity');
    } finally {
      setTogglingActivity(null);
    }
  };

  const handleToggleDefault = async (activityId: string, defaultOn: boolean) => {
    setTogglingActivity(activityId);
    setError(null);
    try {
      await enableActivityMutation.mutateAsync({ userId, activityId, data: { default_on_timesheet: defaultOn } });
      invalidateEnabled();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update default setting');
    } finally {
      setTogglingActivity(null);
    }
  };

  const handleAddRate = (activity: PayrollActivity) => {
    setModalActivity(activity);
    setEditingRate(null);
    setFormData({
      hourly_rate: String(activity.rates?.length ? activity.rates[activity.rates.length - 1].hourly_rate ?? '' : ''),
      start_date: new Date().toLocaleDateString('en-CA'),
    });
    setError(null);
    setShowModal(true);
  };

  const handleEditRate = (activity: PayrollActivity, rate: UserActivityRate) => {
    setModalActivity(activity);
    setEditingRate(rate);
    setFormData({
      hourly_rate: String(rate.hourly_rate ?? ''),
      start_date: rate.start_date || '',
    });
    setError(null);
    setShowModal(true);
  };

  const handleDeleteRate = async (rate: UserActivityRate) => {
    if (!rate.id) return;
    if (!window.confirm('Delete this rate entry?')) return;
    try {
      await deleteRateMutation.mutateAsync({ userId, rateId: rate.id });
      invalidateRates();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete rate');
    }
  };

  const handleSubmitRate = async () => {
    if (!modalActivity?.id) return;
    if (!formData.hourly_rate) { setError('Hourly rate is required'); return; }
    if (!formData.start_date) { setError('Start date is required'); return; }

    setSaving(true);
    setError(null);
    try {
      if (editingRate?.id) {
        await updateRateMutation.mutateAsync({
          userId,
          rateId: editingRate.id,
          data: {
            hourly_rate: parseFloat(formData.hourly_rate),
            start_date: formData.start_date,
          },
        });
      } else {
        await createRateMutation.mutateAsync({
          userId,
          data: {
            activity_id: modalActivity.id,
            hourly_rate: parseFloat(formData.hourly_rate),
            start_date: formData.start_date,
          },
        });
      }
      invalidateRates();
      setShowModal(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save rate');
    } finally {
      setSaving(false);
    }
  };

  const isLoading = ratesLoading || enabledLoading;

  if (isLoading) {
    return (
      <tr>
        <td colSpan={7} className="bg-body-tertiary">
          <div className="text-center py-2">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading activities...
          </div>
        </td>
      </tr>
    );
  }

  return (
    <>
      <tr>
        <td colSpan={7} className="p-0">
          <div className="bg-body-tertiary px-4 py-3">
            {error && !showModal && (
              <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-2">
                {error}
              </Alert>
            )}
            <Table size="sm" className="mb-0 bg-body">
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>Enabled</th>
                  <th>Activity</th>
                  <th style={{ width: '120px' }}>Timesheet Default</th>
                  <th>Rate</th>
                  <th>Start Date</th>
                  <th style={{ width: '120px' }}>Rate Actions</th>
                </tr>
              </thead>
              <tbody>
                {activities.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-2">
                      No activities configured yet.
                    </td>
                  </tr>
                ) : (
                  activities.map((activity) => (
                    <ActivityRateRows
                      key={activity.id}
                      activity={activity}
                      userId={userId}
                      isEnabled={enabledActivityIds.has(activity.id || '')}
                      isDefaultOnTimesheet={defaultActivityIds.has(activity.id || '')}
                      isToggling={togglingActivity === (activity.id || '')}
                      isQBPayee={isQBPayee}
                      userRates={ratesByActivity.get(activity.id || '') || []}
                      onToggleActivity={handleToggleActivity}
                      onToggleDefault={handleToggleDefault}
                      onAddRate={handleAddRate}
                      onEditRate={handleEditRate}
                      onDeleteRate={handleDeleteRate}
                    />
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </td>
      </tr>

      {/* Add/Edit Rate Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingRate ? 'Edit' : 'Add'} Rate: {modalActivity?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Form onSubmit={(e) => { e.preventDefault(); handleSubmitRate(); }}>
            <Form.Group className="mb-3">
              <Form.Label>Hourly Rate ($)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={formData.hourly_rate}
                onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
              />
              <Form.Text className="text-muted">
                This rate applies to timesheet entries on or after this date.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmitRate} disabled={saving}>
            {saving ? (
              <><Spinner animation="border" size="sm" className="me-1" />Saving...</>
            ) : (
              editingRate ? 'Update' : 'Create'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// Sales quotas modal for a user
const UserSalesQuotasModal: React.FC<{
  show: boolean;
  onHide: () => void;
  userId: string;
  userName: string;
}> = ({ show, onHide, userId, userName }) => {
  const queryClient = useQueryClient();
  const { data: quotasData, isLoading } = useGetUsersUserIdSalesQuota(userId, { query: { enabled: show } });
  const quotas: SalesQuota[] = (quotasData as any)?.data || [];
  const putQuotaMutation = usePutUsersUserIdSalesQuota();

  const [editingQuota, setEditingQuota] = useState<SalesQuota | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [busy, setBusy] = useState(false);

  type SortKey = 'role' | 'period' | 'repeat' | 'quota' | 'base_rate' | 'over_quota_rate' | 'person_name';
  const [sortKey, setSortKey] = useState<SortKey>('period');
  const [sortAsc, setSortAsc] = useState(true);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const sortedQuotas = useMemo(() => {
    const sorted = [...quotas];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'role': cmp = (a.role || '').localeCompare(b.role || ''); break;
        case 'period': cmp = (a.period_start || '').localeCompare(b.period_start || ''); break;
        case 'repeat': cmp = (a.repeat || '').localeCompare(b.repeat || ''); break;
        case 'quota': cmp = parseFloat(a.quota_amount || '0') - parseFloat(b.quota_amount || '0'); break;
        case 'base_rate': cmp = parseFloat(a.base_rate || '0') - parseFloat(b.base_rate || '0'); break;
        case 'over_quota_rate': cmp = parseFloat(a.over_quota_rate || '0') - parseFloat(b.over_quota_rate || '0'); break;
        case 'person_name': cmp = (a.person_name || '').localeCompare(b.person_name || ''); break;
      }
      return sortAsc ? cmp : -cmp;
    });
    return sorted;
  }, [quotas, sortKey, sortAsc]);

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: getGetUsersUserIdSalesQuotaQueryKey(userId) });
  };

  const formatCurrency = (value: string | undefined) => {
    if (!value) return '$0.00';
    const num = parseFloat(value);
    return isNaN(num) ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  };

  const formatPercent = (value: string | undefined) => {
    if (!value) return '0%';
    const num = parseFloat(value);
    return isNaN(num) ? '0%' : (num * 100).toFixed(1) + '%';
  };

  const handleSave = async (quota: SalesQuota) => {
    try {
      await putQuotaMutation.mutateAsync({ userId, data: quota });
      queryClient.invalidateQueries({ queryKey: getGetUsersUserIdSalesQuotaQueryKey(userId) });
      setShowEditForm(false);
      setEditingQuota(null);
    } catch (err) {
      console.error('Failed to save quota:', err);
    }
  };

  const handleDelete = async (quota: SalesQuota) => {
    if (!quota.id) return;
    if (!window.confirm(`Delete this ${quota.role || 'quota'} (${quota.period_start}..${quota.period_end})?`)) return;
    try {
      await deleteCommissionsQuotasId(quota.id);
      queryClient.invalidateQueries({ queryKey: getGetUsersUserIdSalesQuotaQueryKey(userId) });
    } catch (err) {
      console.error('Failed to delete quota:', err);
    }
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Sales Quotas — {userName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3 d-flex gap-2 align-items-center">
          <Button variant="primary" size="sm" onClick={() => {
            setEditingQuota({
              user_id: userId,
              person_name: userName,
              role: 'salesperson' as any,
              period_start: new Date().toLocaleDateString('en-CA'),
              period_end: '',
              repeat: 'monthly',
              quota_amount: '',
              base_rate: '0.1',
              over_quota_rate: '0.12',
            });
            setShowEditForm(true);
          }}>
            <i className="bi bi-plus me-1"></i>Add New
          </Button>

        </div>

        {showEditForm && editingQuota && (
          <Card className="mb-3">
            <Card.Header>{editingQuota.id ? 'Edit Quota' : 'New Quota'}</Card.Header>
            <Card.Body>
              <QuotaInlineForm quota={editingQuota} onSave={handleSave} onCancel={() => { setShowEditForm(false); setEditingQuota(null); }} />
            </Card.Body>
          </Card>
        )}

        {isLoading ? (
          <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
        ) : quotas.length === 0 ? (
          <Alert variant="info">No sales quotas configured for this user.</Alert>
        ) : (
          <Table size="sm" bordered hover>
            <thead><tr>
              {([
                ['role', 'Role', ''],
                ['period', 'Period', ''],
                ['repeat', 'Repeat', ''],
                ['quota', 'Quota', 'text-end'],
                ['base_rate', 'Base Rate', 'text-end'],
                ['over_quota_rate', 'Over Quota', 'text-end'],
              ] as [SortKey, string, string][]).map(([key, label, cls]) => (
                <th key={key} className={`${cls} user-select-none`} style={{ cursor: 'pointer' }} onClick={() => handleSort(key)}>
                  {label} {sortKey === key ? (sortAsc ? '\u25B2' : '\u25BC') : ''}
                </th>
              ))}
              <th className="user-select-none" style={{ cursor: 'pointer' }} onClick={() => handleSort('person_name')}>
                Name {sortKey === 'person_name' ? (sortAsc ? '\u25B2' : '\u25BC') : ''}
              </th><th>Actions</th>
            </tr></thead>
            <tbody>
              {sortedQuotas.map((q, i) => (
                <tr key={q.id || i}>
                  <td><Badge bg={q.role === 'sales_lead' ? 'info' : 'primary'}>{q.role === 'sales_lead' ? 'Lead' : 'Sales'}</Badge></td>
                  <td>{q.period_start} — {q.period_end}</td>
                  <td>{q.repeat || 'one-time'}</td>
                  <td className="text-end">{formatCurrency(q.quota_amount)}</td>
                  <td className="text-end">{formatPercent(q.base_rate)}</td>
                  <td className="text-end">{formatPercent(q.over_quota_rate)}</td>
                  <td>{q.person_name}</td>
                  <td className="d-flex gap-1">
                    <Button variant="outline-primary" size="sm" onClick={() => { setEditingQuota(q); setShowEditForm(true); }} title="Edit">
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(q)} title="Delete">
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
    </Modal>
  );
};

// Inline form for creating/editing a quota
const QuotaInlineForm: React.FC<{
  quota: SalesQuota;
  onSave: (q: SalesQuota) => void;
  onCancel: () => void;
}> = ({ quota, onSave, onCancel }) => {
  const [formData, setFormData] = useState<SalesQuota>({ ...quota });

  return (
    <Form>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Role</Form.Label>
            <Form.Select size="sm" value={(formData.role as string) || 'salesperson'} onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}>
              <option value="salesperson">Salesperson</option>
              <option value="sales_lead">Sales Lead</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Period Start (inclusive)</Form.Label>
            <Form.Control size="sm" type="date" value={formData.period_start || ''} onChange={(e) => setFormData({ ...formData, period_start: e.target.value })} />
            <Form.Text className="text-muted small">Begins 00:00 business time.</Form.Text>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Period End (exclusive)</Form.Label>
            <Form.Control size="sm" type="date" value={formData.period_end || ''} onChange={(e) => setFormData({ ...formData, period_end: e.target.value })} />
            <Form.Text className="text-muted small">Ends 00:00 business time — last day covered is the prior date. Must be after Start.</Form.Text>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={3}>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Repeat</Form.Label>
            <Form.Select size="sm" value={formData.repeat || ''} onChange={(e) => setFormData({ ...formData, repeat: e.target.value })}>
              <option value="">One-time</option>
              <option value="monthly">Monthly</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Quota ($)</Form.Label>
            <Form.Control size="sm" type="number" step="0.01" value={formData.quota_amount || ''} onChange={(e) => setFormData({ ...formData, quota_amount: e.target.value })} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Base Rate (%)</Form.Label>
            <Form.Control size="sm" type="number" step="0.01" value={formData.base_rate ? (parseFloat(formData.base_rate) * 100).toString() : ''} onChange={(e) => setFormData({ ...formData, base_rate: (parseFloat(e.target.value) / 100).toString() })} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Over Quota Rate (%)</Form.Label>
            <Form.Control size="sm" type="number" step="0.01" value={formData.over_quota_rate ? (parseFloat(formData.over_quota_rate) * 100).toString() : ''} onChange={(e) => setFormData({ ...formData, over_quota_rate: (parseFloat(e.target.value) / 100).toString() })} />
          </Form.Group>
        </Col>
      </Row>
      <div className="d-flex gap-2 mt-2">
        <Button size="sm" variant="primary" onClick={() => onSave(formData)}>Save</Button>
        <Button size="sm" variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </Form>
  );
};

// ManagePayeesModal lets an admin manage the set of counterparties that pay
// a given user. "Linked" payees (payees.user_id == user.id) are used by the
// bank-sync to attribute incoming commission payments. One of the linked
// payees can be designated "primary" — that's the user_timesheet_configs
// .payee_id, used as the default outgoing-payment target.
//
// Edits are batched: the user adds/removes/repromotes locally, then Save
// commits the diff (or Cancel discards). On Save, three kinds of updates
// can fire: payee→user link (new linked payees), payee→user unlink (removed
// linked payees), and primary-payee swap (user_timesheet_configs.payee_id).
interface ManagePayeesModalProps {
  show: boolean;
  onHide: () => void;
  user: User;
  primaryPayeeId: string | null;
  payees: Payee[];
  onSetPrimary: (payeeId: string | null) => Promise<void>;
  onLink: (payeeId: string, userId: string) => Promise<void>;
  onUnlink: (payeeId: string) => Promise<void>;
  saving: boolean;
}

const ManagePayeesModal: React.FC<ManagePayeesModalProps> = ({
  show, onHide, user, primaryPayeeId, payees,
  onSetPrimary, onLink, onUnlink, saving,
}) => {
  // Reset local state every time the modal opens so a previous Cancel
  // doesn't leak stale edits into the next session.
  const initiallyLinkedIds = useMemo(
    () => new Set(payees.filter(p => p.user_id === user.id).map(p => p.id || '')),
    [payees, user.id]
  );

  const [linkedIds, setLinkedIds] = useState<Set<string>>(initiallyLinkedIds);
  const [localPrimary, setLocalPrimary] = useState<string | null>(primaryPayeeId);

  useEffect(() => {
    if (show) {
      setLinkedIds(new Set(initiallyLinkedIds));
      setLocalPrimary(primaryPayeeId);
    }
  }, [show, initiallyLinkedIds, primaryPayeeId]);

  // Lookup tables for rendering rows by id.
  const payeeById = useMemo(() => {
    const m = new Map<string, Payee>();
    for (const p of payees) {
      if (p.id) m.set(p.id, p);
    }
    return m;
  }, [payees]);

  const linkedRows = useMemo(
    () => Array.from(linkedIds)
      .map(id => payeeById.get(id))
      .filter((p): p is Payee => !!p)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [linkedIds, payeeById]
  );

  // Pickable: anything not currently in the local linked set, and not
  // already linked to a different user.
  const unlinkedOptions = useMemo(
    () => payees
      .filter(p => !linkedIds.has(p.id || ''))
      .filter(p => !p.user_id || p.user_id === user.id)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(p => ({ value: p.id || '', label: p.name || '', payee: p })),
    [payees, linkedIds, user.id]
  );

  const sourceLabel = (s?: string) => s === 'mercury' ? 'Mercury' : s === 'quickbooks' ? 'QuickBooks' : s === 'file' ? 'File Bank Account' : (s || '');

  const filterPayee = (option: { data: { payee: Payee } }, q: string) => {
    const needle = q.toLowerCase();
    const p = option.data.payee;
    if ((p.name || '').toLowerCase().includes(needle)) return true;
    if (p.nickname && p.nickname.toLowerCase().includes(needle)) return true;
    if (p.emails?.some(e => e.toLowerCase().includes(needle))) return true;
    return false;
  };

  const handleAdd = (payeeId: string) => {
    setLinkedIds(prev => {
      const next = new Set(prev);
      next.add(payeeId);
      return next;
    });
  };

  const handleRemove = (payeeId: string) => {
    setLinkedIds(prev => {
      const next = new Set(prev);
      next.delete(payeeId);
      return next;
    });
    if (localPrimary === payeeId) {
      setLocalPrimary(null);
    }
  };

  // Diff vs initial state. Driving Save's API call set.
  const additions = useMemo(
    () => Array.from(linkedIds).filter(id => !initiallyLinkedIds.has(id)),
    [linkedIds, initiallyLinkedIds]
  );
  const removals = useMemo(
    () => Array.from(initiallyLinkedIds).filter(id => !linkedIds.has(id)),
    [linkedIds, initiallyLinkedIds]
  );
  const primaryChanged = localPrimary !== primaryPayeeId;
  const isDirty = additions.length > 0 || removals.length > 0 || primaryChanged;

  const handleSave = async () => {
    // Order matters: link new payees first (so the primary can reference
    // them), then update the primary, then unlink removed payees (so an
    // unlinked-as-primary case is already handled by primary swap).
    for (const id of additions) {
      await onLink(id, user.id || '');
    }
    if (primaryChanged) {
      await onSetPrimary(localPrimary);
    }
    for (const id of removals) {
      await onUnlink(id);
    }
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop={saving ? 'static' : true}>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>
          <i className="bi bi-link-45deg me-2" />
          Linked counterparties — {user.first_name} {user.last_name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          A counterparty (Mercury/QuickBooks payee) linked here is treated as paying this user — used by the bank-sync to attribute incoming commission payments. One linked counterparty may be marked <strong>Primary</strong>; that's the default target for outgoing contractor-payroll payments.
        </p>

        <Table hover size="sm" className="mb-3">
          <thead>
            <tr>
              <th>Counterparty</th>
              <th style={{ width: '120px' }}>Source</th>
              <th style={{ width: '90px' }} className="text-center">Primary</th>
              <th style={{ width: '60px' }}></th>
            </tr>
          </thead>
          <tbody>
            {linkedRows.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-muted text-center py-3 fst-italic">
                  No counterparties linked yet.
                </td>
              </tr>
            ) : linkedRows.map(p => (
              <tr key={p.id}>
                <td>
                  <div>
                    {p.name}
                    {!initiallyLinkedIds.has(p.id || '') && (
                      <Badge bg="success" className="ms-2">new</Badge>
                    )}
                  </div>
                  {p.nickname && <small className="text-muted">{p.nickname}</small>}
                </td>
                <td><Badge bg="secondary">{sourceLabel(p.source)}</Badge></td>
                <td className="text-center">
                  <Form.Check
                    type="radio"
                    name={`primary-${user.id}`}
                    checked={localPrimary === p.id}
                    disabled={saving}
                    onChange={() => setLocalPrimary(p.id || null)}
                    aria-label="Mark as primary"
                  />
                </td>
                <td className="text-end">
                  <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={saving}
                    onClick={() => handleRemove(p.id || '')}
                    title="Remove this counterparty"
                  >
                    <i className="bi bi-x-lg" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <div>
          <h6 className="mb-2">Add a counterparty</h6>
          {unlinkedOptions.length === 0 ? (
            <p className="text-muted small fst-italic mb-0">No more payees available to link.</p>
          ) : (
            <Select
              value={null as any}
              onChange={(opt: any) => opt && opt.value && handleAdd(opt.value)}
              options={unlinkedOptions}
              isDisabled={saving}
              placeholder="Search unlinked payees..."
              filterOption={filterPayee as any}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              formatOptionLabel={(option: any) => (
                <div>
                  <div>{option.payee.name}</div>
                  <small className="text-muted">{sourceLabel(option.payee.source)}{option.payee.nickname ? ` · ${option.payee.nickname}` : ''}</small>
                </div>
              )}
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !isDirty}>
          {saving ? <><Spinner size="sm" animation="border" className="me-2" />Saving…</> : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Main component
const PayrollUserConfigTab: React.FC = () => {
  const queryClient = useQueryClient();

  // Cursor-paginated user list. The page renders all users in a single
  // table (no infinite scroll), so we auto-fetch every page on load via
  // the effect below.
  const buildQueryParams = useCallback((cursor: string | undefined) => {
    if (cursor) {
      return { cursor, limit: FETCH_LIMIT };
    }
    return { sort_field: 'first_name', sort_direction: 'asc', limit: FETCH_LIMIT };
  }, []);
  // Row identity for the hook's cross-page duplicate-detection. This page
  // doesn't render users in a DataTable, but the hook still requires a
  // row-key fn — by the same rule any other caller follows.
  const getRowKey = useCallback((u: User) => u.id || '', []);

  const {
    allItems: users,
    isLoading: usersLoading,
    hasMore: usersHasMore,
    handleLoadMore: loadMoreUsers,
  } = usePaginatedQuery<User, any, undefined>({
    queryKey: 'payroll-user-config:users',
    buildQueryParams,
    queryFn: (params) => getUsers(params as GetUsersParams),
    getRowKey,
  });
  useEffect(() => {
    if (usersHasMore && !usersLoading) {
      loadMoreUsers();
    }
  }, [usersHasMore, usersLoading, loadMoreUsers]);

  const { data: configsData, isLoading: configsLoading } = useGetPayrollUsersConfigs({});
  const { data: activitiesData, isLoading: activitiesLoading } = useGetPayrollActivitiesAll();
  const { data: payeesData } = useGetPayees();
  const updateConfigMutation = usePutPayrollUsersUserIdConfig();
  const setPayeeUserMutation = usePutPayeesIdUser();

  const [error, setError] = useState<string | null>(null);
  const [updatingUser, setUpdatingUser] = useState<string | null>(null);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [quotaModalUser, setQuotaModalUser] = useState<{ id: string; name: string } | null>(null);
  const [managingPayeesUser, setManagingPayeesUser] = useState<User | null>(null);
  const [savingPayees, setSavingPayees] = useState(false);
  const configs = configsData?.data || [];
  const activities = (activitiesData?.data || []).filter(a => a.active);
  const payees: Payee[] = payeesData?.data || [];

  const configByUserId = new Map<string, UserTimesheetConfig>();
  for (const config of configs) {
    if (config.user_id) {
      configByUserId.set(config.user_id, config);
    }
  }

  // react-select options for payees
  interface PayeeOption {
    value: string;
    label: string;
    payee: Payee;
  }

  const payeeOptions: PayeeOption[] = useMemo(() =>
    payees.map(p => ({
      value: p.id || '',
      label: p.name || '',
      payee: p,
    })),
    [payees]
  );

  const handleToggleEnabled = async (userId: string, enabled: boolean) => {
    setUpdatingUser(userId);
    setError(null);
    try {
      await updateConfigMutation.mutateAsync({
        userId,
        data: { timesheets_enabled: enabled },
      });
      queryClient.invalidateQueries({ queryKey: getGetPayrollUsersConfigsQueryKey() });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update config');
    } finally {
      setUpdatingUser(null);
    }
  };

  const handlePayPeriodChange = async (userId: string, payPeriodType: PayPeriodType) => {
    setUpdatingUser(userId);
    setError(null);
    try {
      await updateConfigMutation.mutateAsync({
        userId,
        data: { pay_period_type: payPeriodType },
      });
      queryClient.invalidateQueries({ queryKey: getGetPayrollUsersConfigsQueryKey() });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update config');
    } finally {
      setUpdatingUser(null);
    }
  };

  const handleDefaultRateChange = async (userId: string, value: string) => {
    setUpdatingUser(userId);
    setError(null);
    try {
      const rate = value.trim() === '' ? 0 : parseFloat(value);
      await updateConfigMutation.mutateAsync({
        userId,
        data: { default_hourly_rate: rate },
      });
      queryClient.invalidateQueries({ queryKey: getGetPayrollUsersConfigsQueryKey() });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update default rate');
    } finally {
      setUpdatingUser(null);
    }
  };

  const handlePayeeChange = async (userId: string, option: PayeeOption | null) => {
    setUpdatingUser(userId);
    setError(null);
    try {
      await updateConfigMutation.mutateAsync({
        userId,
        data: { payee_id: option?.value || '' },
      });
      queryClient.invalidateQueries({ queryKey: getGetPayrollUsersConfigsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetPayrollUsersUserIdActivitiesQueryKey(userId) });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update payee');
    } finally {
      setUpdatingUser(null);
    }
  };

  // Set or clear the primary payee for a user (writes user_timesheet_configs.payee_id).
  const handleSetPrimaryPayee = useCallback(async (userId: string, payeeId: string | null) => {
    setSavingPayees(true);
    setError(null);
    try {
      await updateConfigMutation.mutateAsync({
        userId,
        data: { payee_id: payeeId || '' },
      });
      queryClient.invalidateQueries({ queryKey: getGetPayrollUsersConfigsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetPayrollUsersUserIdActivitiesQueryKey(userId) });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to set primary payee');
    } finally {
      setSavingPayees(false);
    }
  }, [queryClient, updateConfigMutation]);

  // Link a payee to a user (writes payees.user_id).
  const handleLinkPayee = useCallback(async (payeeId: string, userId: string) => {
    setSavingPayees(true);
    setError(null);
    try {
      await setPayeeUserMutation.mutateAsync({ id: payeeId, data: { user_id: userId } });
      queryClient.invalidateQueries({ queryKey: getGetPayeesQueryKey() });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to link payee');
    } finally {
      setSavingPayees(false);
    }
  }, [queryClient, setPayeeUserMutation]);

  // Unlink a payee from its user (writes payees.user_id = null). If the
  // unlinked payee was also the user's primary, clear the primary too.
  const handleUnlinkPayee = useCallback(async (payeeId: string) => {
    setSavingPayees(true);
    setError(null);
    try {
      await setPayeeUserMutation.mutateAsync({ id: payeeId, data: { user_id: null } });
      // Find any user whose primary payee was this one and clear it.
      for (const c of configs) {
        if (c.payee_id === payeeId && c.user_id) {
          await updateConfigMutation.mutateAsync({ userId: c.user_id, data: { payee_id: '' } });
        }
      }
      queryClient.invalidateQueries({ queryKey: getGetPayeesQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetPayrollUsersConfigsQueryKey() });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to unlink payee');
    } finally {
      setSavingPayees(false);
    }
  }, [configs, queryClient, setPayeeUserMutation, updateConfigMutation]);

  const toggleExpanded = (userId: string) => {
    setExpandedUserId(prev => prev === userId ? null : userId);
  };

  // Custom filter: search across name, nickname, and emails
  const filterPayeeOption = (option: { data: PayeeOption }, inputValue: string) => {
    const q = inputValue.toLowerCase();
    const p = option.data.payee;
    if ((p.name || '').toLowerCase().includes(q)) return true;
    if (p.nickname && p.nickname.toLowerCase().includes(q)) return true;
    if (p.emails?.some(e => e.toLowerCase().includes(q))) return true;
    return false;
  };

  const isLoading = usersLoading || configsLoading || activitiesLoading;

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading user configuration...
      </div>
    );
  }

  const activeUsers = users.filter(u => u.active);

  return (
    <>
    <Card className="">
      <Card.Body>
        <h5 className="mb-3">User Timesheet Configuration</h5>
        <p className="text-muted mb-3">
          Enable or disable timesheets for each user, configure their pay period type,
          and expand a row to manage which activities are available and their rates.
        </p>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Table hover responsive>
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th>User</th>
              <th>Role</th>
              <th>Timesheets Enabled</th>
              <th>Pay Period</th>
              <th>Hourly Rate</th>
              {payees.length > 0 && <th>Payee</th>}
              <th>Quotas</th>
            </tr>
          </thead>
          <tbody>
            {activeUsers.length === 0 ? (
              <tr>
                <td colSpan={payees.length > 0 ? 8 : 7} className="text-center text-muted py-4">
                  No active users found.
                </td>
              </tr>
            ) : (
              activeUsers.map((user) => {
                const config = configByUserId.get(user.id || '');
                const enabled = config?.timesheets_enabled ?? false;
                const payPeriod = config?.pay_period_type ?? PayPeriodType.PayPeriodTypeBiweekly;
                const currentPayeeId = config?.payee_id || '';
                const isUpdating = updatingUser === user.id;
                const isExpanded = expandedUserId === user.id;

                const selectedPayeeOption = payeeOptions.find(o => o.value === currentPayeeId) || null;

                return (
                  <React.Fragment key={user.id}>
                    <tr
                      style={{ cursor: 'pointer' }}
                      className={isExpanded ? 'table-active' : ''}
                    >
                      <td onClick={() => toggleExpanded(user.id!)}>
                        <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                      </td>
                      <td onClick={() => toggleExpanded(user.id!)}>
                        {user.first_name} {user.last_name}
                        <br />
                        <small className="text-muted">{user.email}</small>
                      </td>
                      <td onClick={() => toggleExpanded(user.id!)}>
                        <Badge bg={getRoleBadgeVariant(user.role || '')}>{getRoleDisplayName(user.role || '')}</Badge>
                      </td>
                      <td>
                        <Form.Check
                          type="switch"
                          checked={enabled}
                          disabled={isUpdating}
                          onChange={(e) => handleToggleEnabled(user.id!, e.target.checked)}
                        />
                      </td>
                      <td>
                        <Form.Select
                          size="sm"
                          value={payPeriod}
                          disabled={isUpdating || !enabled}
                          onChange={(e) => handlePayPeriodChange(user.id!, e.target.value as PayPeriodType)}
                          style={{ width: '140px' }}
                        >
                          <option value={PayPeriodType.PayPeriodTypeBiweekly}>Biweekly</option>
                          <option value={PayPeriodType.PayPeriodTypeMonthly}>Monthly</option>
                        </Form.Select>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <InputGroup size="sm" style={{ width: '150px' }}>
                          <InputGroup.Text>$</InputGroup.Text>
                          <Form.Control
                            type="text"
                            placeholder="Per Activity"
                            disabled={isUpdating || !enabled}
                            defaultValue={config?.default_hourly_rate != null ? config.default_hourly_rate.toFixed(2) : ''}
                            onInput={(e) => {
                              const input = e.target as HTMLInputElement;
                              const val = input.value.replace(/[^0-9.]/g, '');
                              const dotIdx = val.indexOf('.');
                              if (dotIdx !== -1) {
                                input.value = val.slice(0, dotIdx + 3);
                              } else {
                                input.value = val;
                              }
                            }}
                            onBlur={(e) => {
                              const raw = e.target.value.trim();
                              const currentVal = config?.default_hourly_rate;
                              if (raw === '') {
                                if (currentVal != null) handleDefaultRateChange(user.id!, '');
                                return;
                              }
                              const num = parseFloat(raw);
                              if (isNaN(num)) {
                                e.target.value = currentVal != null ? currentVal.toFixed(2) : '';
                                return;
                              }
                              e.target.value = num.toFixed(2);
                              if (num !== currentVal) handleDefaultRateChange(user.id!, String(num));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                            }}
                          />
                        </InputGroup>
                      </td>
                      {payees.length > 0 && (
                        <td onClick={(e) => e.stopPropagation()}>
                          {(() => {
                            const linkedForUser = payees.filter(p => p.user_id === user.id);
                            const primaryPayee = linkedForUser.find(p => p.id === currentPayeeId) || null;
                            const extras = linkedForUser.length - (primaryPayee ? 1 : 0);
                            const summary = linkedForUser.length === 0
                              ? 'Not linked'
                              : primaryPayee
                                ? (extras > 0 ? `${primaryPayee.name} +${extras}` : (primaryPayee.name || ''))
                                : `${linkedForUser.length} linked (no primary)`;
                            return (
                              <Button
                                variant={linkedForUser.length === 0 ? 'outline-secondary' : 'outline-primary'}
                                size="sm"
                                onClick={() => setManagingPayeesUser(user)}
                                style={{ minWidth: '220px', textAlign: 'left' }}
                              >
                                <i className="bi bi-link-45deg me-2" />
                                {summary}
                              </Button>
                            );
                          })()}
                        </td>
                      )}
                      <td onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => setQuotaModalUser({ id: user.id!, name: `${user.first_name || ''} ${user.last_name || ''}`.trim() })}
                        >
                          <i className="bi bi-gear me-1"></i>Sales Quotas
                        </Button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <UserActivitiesSection userId={user.id!} activities={activities} isQBPayee={config?.payee?.source === 'quickbooks'} />
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </Table>
      </Card.Body>
    </Card>

    {quotaModalUser && (
      <UserSalesQuotasModal
        show={!!quotaModalUser}
        onHide={() => setQuotaModalUser(null)}
        userId={quotaModalUser.id}
        userName={quotaModalUser.name}
      />
    )}

    {managingPayeesUser && (
      <ManagePayeesModal
        show={!!managingPayeesUser}
        onHide={() => setManagingPayeesUser(null)}
        user={managingPayeesUser}
        primaryPayeeId={configByUserId.get(managingPayeesUser.id || '')?.payee_id || null}
        payees={payees}
        saving={savingPayees}
        onSetPrimary={(payeeId) => handleSetPrimaryPayee(managingPayeesUser.id || '', payeeId)}
        onLink={handleLinkPayee}
        onUnlink={handleUnlinkPayee}
      />
    )}
    </>
  );
};

export default PayrollUserConfigTab;
